import { useState, useEffect } from "react";
import { TagsAutocomplete } from "./TagsAutocomplete";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import type { FilterDescriptor } from "@/lib/erp";

type TranslationModule = Record<string, string>;

interface TagsFilterProps {
  value: FilterDescriptor[];
  onChange: (filters: FilterDescriptor[]) => void;
  translations?: TranslationModule;
}

export function TagsFilter({
  value,
  onChange,
  translations,
}: TagsFilterProps) {
  const [tags, setTags] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState("");

  // Extract tags from filters on mount/change
  useEffect(() => {
    const tagFilters = value.filter(
      (f) => f.field === "_user_tags" && f._metadata?.section === "tags"
    );
    const extractedTags = tagFilters
      .map((f) => {
        // Extract tag name from value format: %tagname%
        const match = String(f.value).match(/%(.+?)%/);
        return match ? match[1].trim().toLowerCase() : null; // Normalize: lowercase, trimmed
      })
      .filter((t): t is string => t !== null);

    setTags(extractedTags);
  }, [value]);

  const handleTagAdd = (newTag: string) => {
    // Normalize tag: lowercase, trimmed
    const normalized = newTag.trim().toLowerCase();
    if (!normalized || tags.includes(normalized)) return;
    
    const updatedTags = [...tags, normalized];
    setTags(updatedTags);
    updateFilters(updatedTags);
    setCurrentInput("");
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = tags.filter((t) => t !== tagToRemove);
    setTags(updatedTags);
    updateFilters(updatedTags);
  };

  const updateFilters = (tagList: string[]) => {
    // Remove existing tags filters
    const otherFilters = value.filter(
      (f) => !(f.field === "_user_tags" && f._metadata?.section === "tags")
    );

    // Create new filters for each tag (normalized)
    const newFilters: FilterDescriptor[] = tagList.map((tag) => ({
      field: "_user_tags",
      operator: "like",
      value: `%${tag}%`, // Tag is already normalized
      _metadata: {
        section: "tags",
      },
    }));

    onChange([...otherFilters, ...newFilters]);
  };

  const t = translations || {};

  return (
    <div className="space-y-2">
      <TagsAutocomplete
        value={currentInput}
        onChange={handleTagAdd}
        existingTags={tags}
        placeholder={t.enterTagNames || "Enter tag names..."}
        translations={translations}
      />

      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {tags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="flex items-center gap-1 px-2 py-1"
            >
              <span className="text-xs">{tag}</span>
              <button
                onClick={() => handleRemoveTag(tag)}
                className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                aria-label={t.removeTag || "Remove tag"}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

